import { cn } from "@faire/prototype-playground/lib/utils";
import { Button } from "@faire/slate/components/button";

import { RESULT_STEER_OPTIONS, type ResultSteer } from "./searchModel";

interface ResultSteerRowProps {
  /**
   * The steer this row produced, once it has been spent. `undefined` while the
   * row is still live, or when the next batch came from a plain scroll.
   */
  selected: ResultSteer | undefined;
  /** This row has already produced its batch; it stays as a record of the choice. */
  spent: boolean;
  onSelect: (steer: ResultSteer) => void;
}

/**
 * The four "where next?" options, inline in the feed after each loaded batch.
 *
 * Pressing one loads the next batch straight away, steered. Scrolling past the
 * row instead loads it unsteered — so the row is a shortcut, never a gate.
 *
 * Selection is carried by Slate's own variants: the chosen steer fills in as
 * `primary`, the rest stay `secondary`.
 *
 * Nothing is ever passed Slate's `disabled` prop. Its disabled styling paints a
 * flat grey fill with no border, which reads as a skeleton placeholder rather
 * than a button. Only spent rows are made inert, and on the wrapper, so every
 * row always looks like ordinary secondary buttons. An in-flight load is *not*
 * a reason to go inert — the sentinel sits just below this row, so a load is
 * often already running by the time you reach it, and blocking on that made the
 * live row impossible to click. `loadBatch` guards against double-loads itself.
 */
export function ResultSteerRow({
  selected,
  spent,
  onSelect,
}: ResultSteerRowProps) {
  return (
    <div className="pt-12 pb-4">
      <p className="type-fs-paragraph text-fs-text-subdued mb-4 text-center">
        Steer your results
      </p>
      <div
        className={cn(
          "grid grid-cols-2 gap-3 md:grid-cols-4",
          spent ? "pointer-events-none" : ""
        )}
      >
        {RESULT_STEER_OPTIONS.map((option) => {
          const isSelected = selected === option.key;
          return (
            <Button
              key={option.key}
              aria-disabled={spent || undefined}
              aria-pressed={isSelected}
              fullWidth
              onClick={() => onSelect(option.key)}
              // Slate's next size step up from `small`'s 40px. Taller than it
              // was, on the scale, and matching the CTA's height.
              size="medium"
              tabIndex={spent ? -1 : undefined}
              variant={isSelected ? "primary" : "secondary"}
            >
              {option.title}
            </Button>
          );
        })}
      </div>
    </div>
  );
}
